import { createApp } from "vue";
import "./bootstrap";

// Import main components
import NucleoMapApp from "./components/NucleoMapApp.vue";

// Import utility libraries
import { debounce } from "lodash";
import moment from "moment";

// Import Alert system
import { SimpleAlert } from "./libs/Alert";

console.log(
  "🗺️ " + (window.Laravel?.appName || "Application") + " is initializing..."
);

// Create Vue application instance
const app = createApp({
  data() {
    return {
      isAppReady: false,
      googleMapsReady: false,
      appError: null,
    };
  },

  components: {
    NucleoMapApp,
  },

  template: `
        <div>
            <!-- Show loading state when app is not ready -->
            <div v-if="!isAppReady" class="flex items-center justify-center min-h-screen bg-gray-50">
                <div class="text-center">
                    <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p class="text-gray-600 text-lg">Initializing application...</p>
                </div>
            </div>
            
            <!-- Application error state -->
            <div v-else-if="appError" class="flex items-center justify-center min-h-screen bg-gray-50">
                <div class="text-center max-w-md">
                    <div class="bg-danger-100 rounded-full p-6 mb-6 mx-auto w-24 h-24 flex items-center justify-center">
                        <i class="fas fa-exclamation-triangle text-3xl text-danger-600"></i>
                    </div>
                    <h3 class="text-xl font-medium text-gray-900 mb-4">Application Initialization Failed</h3>
                    <p class="text-gray-600 mb-6">{{ appError }}</p>
                    <button 
                        @click="retryInit"
                        class="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <i class="fas fa-redo mr-2"></i>
                        Reload
                    </button>
                </div>
            </div>
            
            <!-- Main application component -->
            <NucleoMapApp v-else />
        </div>
    `,

  mounted() {
    this.initializeApp();
  },

  methods: {
    async initializeApp() {
      try {
        console.log("📱 Starting Vue application initialization...");

        // Check for required global variables
        if (!window.mapConfig) {
          throw new Error("Map configuration not found");
        }

        if (!window.initialFilters) {
          throw new Error("Initial filters configuration not found");
        }

        // Wait for Google Maps API to load
        await this.waitForGoogleMaps();

        // Simulate initialization time (for better user experience)
        await new Promise((resolve) => setTimeout(resolve, 500));

        this.isAppReady = true;
        console.log("✅ Vue application initialization completed");

        // Hide initial loading screen
        if (window.hideInitialLoading) {
          window.hideInitialLoading();
        }
      } catch (error) {
        console.error("❌ Application initialization failed:", error);
        this.appError = error.message || "Unknown error";

        // Hide initial loading screen
        if (window.hideInitialLoading) {
          window.hideInitialLoading();
        }
      }
    },

    waitForGoogleMaps() {
      return new Promise((resolve, reject) => {
        // If already loaded, resolve immediately
        if (window.google && window.google.maps) {
          this.googleMapsReady = true;
          console.log("✅ Google Maps API is ready");
          resolve();
          return;
        }

        // Listen for Google Maps load event
        const handleMapsLoaded = () => {
          this.googleMapsReady = true;
          console.log("✅ Google Maps API loaded successfully");
          window.removeEventListener("google-maps-loaded", handleMapsLoaded);
          window.removeEventListener("google-maps-error", handleMapsError);
          resolve();
        };

        const handleMapsError = (event) => {
          console.error("❌ Google Maps API loading failed:", event.detail);
          window.removeEventListener("google-maps-loaded", handleMapsLoaded);
          window.removeEventListener("google-maps-error", handleMapsError);
          reject(
            new Error(event.detail?.message || "Google Maps API loading failed")
          );
        };

        window.addEventListener("google-maps-loaded", handleMapsLoaded);
        window.addEventListener("google-maps-error", handleMapsError);

        // Set timeout
        setTimeout(() => {
          if (!this.googleMapsReady) {
            window.removeEventListener("google-maps-loaded", handleMapsLoaded);
            window.removeEventListener("google-maps-error", handleMapsError);
            reject(new Error("Google Maps API loading timeout"));
          }
        }, 15000);
      });
    },

    retryInit() {
      this.appError = null;
      this.isAppReady = false;
      this.googleMapsReady = false;
      this.initializeApp();
    },
  },
});

// Set global properties
app.config.globalProperties.$alert = SimpleAlert;
app.config.globalProperties.$moment = moment;
app.config.globalProperties.$debounce = debounce;

// Set global variables
app.config.globalProperties.$mapConfig = window.mapConfig || {};
app.config.globalProperties.$initialFilters = window.initialFilters || {};
app.config.globalProperties.$pageConfig = window.pageConfig || {};

// API base configuration
app.config.globalProperties.$apiBaseUrl =
  window.mapConfig?.api_base_url || "/api/public";

// Global error handling
app.config.errorHandler = (err, vm, info) => {
  console.error("Vue global error:", err, info);

  // Show user-friendly error message
  if (SimpleAlert) {
    SimpleAlert.error(
      "Application Error",
      "Please refresh the page or contact technical support"
    );
  }
};

// Global warning handling (development mode)
if (import.meta.env.DEV) {
  app.config.warnHandler = (msg, vm, trace) => {
    console.warn("Vue warning:", msg, trace);
  };
}

// Mount application
const mountApp = () => {
  try {
    const appElement = document.getElementById("nucleo-map-app");
    if (!appElement) {
      throw new Error("Cannot find application mount point #nucleo-map-app");
    }

    // Remove v-cloak hidden class
    appElement.classList.remove("v-cloak--hidden");

    // Mount Vue application
    const mountedApp = app.mount("#nucleo-map-app");

    console.log("🎯 Vue application successfully mounted to #nucleo-map-app");

    // Set global application instance (for debugging)
    if (import.meta.env.DEV) {
      window.vueMapApp = mountedApp;
    }

    return mountedApp;
  } catch (error) {
    console.error("❌ Vue application mounting failed:", error);

    // Show error message
    const appElement = document.getElementById("nucleo-map-app");
    if (appElement) {
      appElement.innerHTML = `
                <div class="flex items-center justify-center min-h-screen bg-gray-50">
                    <div class="text-center max-w-md">
                        <div class="bg-danger-100 rounded-full p-6 mb-6 mx-auto w-24 h-24 flex items-center justify-center">
                            <i class="fas fa-exclamation-triangle text-3xl text-danger-600"></i>
                        </div>
                        <h3 class="text-xl font-medium text-gray-900 mb-4">Application Launch Failed</h3>
                        <p class="text-gray-600 mb-6">${error.message}</p>
                        <button 
                            onclick="location.reload()" 
                            class="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            <i class="fas fa-redo mr-2"></i>
                            Reload Page
                        </button>
                    </div>
                </div>
            `;
      appElement.classList.remove("v-cloak--hidden");
    }

    // Hide initial loading screen
    if (window.hideInitialLoading) {
      window.hideInitialLoading();
    }

    throw error;
  }
};

// Wait for DOM to load before mounting application
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountApp);
} else {
  mountApp();
}

// Export application instance (for testing)
export default app;
